import classNames from 'classnames';
import type { ChangeEvent } from 'react';
import ExitFullScreenIcon from '../../assets/exit-full-screen.svg?react';
import FullScreenIcon from '../../assets/full-screen.svg?react';
import PauseSmallIcon from '../../assets/pause-small.svg?react';
import PlaySmallIcon from '../../assets/play-small.svg?react';
import styles from './FJVideoController.module.less';

interface FJVideoControllerProps {
    /** 裁剪片段总时长（秒） */
    segmentDuration: number;
    /** 当前播放时间（秒，已夹在片段内） */
    clampedTime: number;
    /** 已播放比例 0–1，用于进度条填充宽 */
    progress: number;
    /** 已格式化的 `当前 / 总时长` 文案 */
    timeLabel: string;
    isPlaying: boolean;
    isFullscreen: boolean;
    onTogglePlayPause: () => void;
    onSeek: (e: ChangeEvent<HTMLInputElement>) => void;
    onToggleFullscreen?: () => void;
    disableFullscreen?: boolean;
}

/**
 * 视频预览底栏：进度条 + 播放/暂停、时间、全屏。纯展示，数据与逻辑由父组件注入。
 */
export default function FJVideoController({
    segmentDuration,
    clampedTime,
    progress,
    timeLabel,
    isPlaying,
    isFullscreen,
    onTogglePlayPause,
    onSeek,
    onToggleFullscreen,
    disableFullscreen = false,
}: FJVideoControllerProps) {
    return (
        <div className={styles.fj_video_controller}>
            <div className={styles.progress_bar}>
                <div className={styles.progress_track}>
                    <div className={styles.progress_fill} style={{ width: `${progress * 100}%` }} />
                </div>
                <input
                    type='range'
                    className={styles.input_range}
                    min={0}
                    max={segmentDuration}
                    step={0.1}
                    value={clampedTime}
                    onChange={onSeek}
                    aria-label='progress'
                />
            </div>
            <div className={styles.controls_row}>
                <div className={styles.controls_left}>
                    <button
                        type='button'
                        className={styles.play_btn}
                        onClick={e => {
                            e.stopPropagation();
                            onTogglePlayPause();
                        }}
                        disabled={segmentDuration <= 0}
                        aria-label={isPlaying ? 'pause' : 'play'}
                    >
                        {isPlaying ? (
                            <PauseSmallIcon className={styles.control_svg_small} aria-hidden />
                        ) : (
                            <PlaySmallIcon className={styles.control_svg_small} aria-hidden />
                        )}
                    </button>
                    <span className={styles.time_label}>{timeLabel}</span>
                </div>
                {!disableFullscreen && (
                    <button
                        type='button'
                        className={styles.fullscreen_btn}
                        onClick={e => {
                            e.stopPropagation();
                            onToggleFullscreen?.();
                        }}
                        onKeyDown={e => {
                            if (e.key === ' ' || e.code === 'Space') {
                                e.preventDefault();
                            }
                        }}
                        aria-label={isFullscreen ? 'exit fullscreen' : 'fullscreen'}
                    >
                        {isFullscreen ? (
                            <ExitFullScreenIcon
                                className={classNames(
                                    styles.control_svg_fullscreen,
                                    styles.control_svg_fullscreen_exit,
                                )}
                                aria-hidden
                            />
                        ) : (
                            <FullScreenIcon className={styles.control_svg_fullscreen} aria-hidden />
                        )}
                    </button>
                )}
            </div>
        </div>
    );
}
